/**
 * Parse CSV text with multiline field support
 * @param {string} csvText - Raw CSV text
 * @returns {Array<Array<string>>} - Parsed rows
 */
export function parseCSVText(csvText) {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;
  let i = 0;

  while (i < csvText.length) {
    const char = csvText[i];

    if (char === '"') {
      if (inQuotes && csvText[i + 1] === '"') {
        currentField += '"';
        i += 2;
        continue;
      }
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (currentField || currentRow.length > 0) {
        currentRow.push(currentField.trim());
        rows.push(currentRow);
        currentRow = [];
        currentField = '';
      }
      if (char === '\r' && csvText[i + 1] === '\n') {
        i++;
      }
    } else {
      currentField += char;
    }
    i++;
  }

  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    rows.push(currentRow);
  }

  return rows.filter(row => row.some(field => field.length > 0));
}

/**
 * Group CSV rows by Order ID to combine multi-item orders
 * @param {Array<Object>} rows - Parsed CSV data rows
 * @returns {Array<Object>} - Grouped orders
 */
export function groupRowsByOrderId(rows) {
  const orderMap = new Map();

  rows.forEach(row => {
    const orderId = (row['Order ID'] || '').trim();
    if (!orderId) return;

    if (!orderMap.has(orderId)) {
      orderMap.set(orderId, {
        orderId: orderId,
        baseRow: row,
        items: []
      });
    }

    const productName = (row['Product Name'] || '').trim();
    const quantity = parseInt(row['Quantity']) || 1;
    const weightKg = parseFloat(row['Weight(kg)']) || 0;
    const skuId = (row['Package ID'] || '').trim() + "___" + (row['Seller SKU'] || '').trim();

    if (productName) {
      orderMap.get(orderId).items.push({
        sku: skuId,
        name: productName,
        qty: quantity,
        weightKg: weightKg,
        variation: (row['Variation'] || '').trim()
      });
    }
  });

  return Array.from(orderMap.values());
}

/**
 * Parse full CSV content and return structured data
 * @param {string} csvText - Raw CSV text
 * @returns {Object} - Parsed CSV data and grouped orders
 */
export function parseCSV(csvText) {
  const rows = parseCSVText(csvText);
  const headers = rows[0].map((h, index) => {
    let header = h.replace(/"/g, '').trim();
    if (index === 0) {
      header = header.replace(/^\ufeff/, ''); // Strip BOM
    }
    return header;
  });

  const csvData = [];
  for (let i = 1; i < rows.length; i++) {
    if (rows[i].length > 0) {
      const values = rows[i];
      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      csvData.push(row);
    }
  }

  const groupedOrders = groupRowsByOrderId(csvData);

  return {
    csvData,
    groupedOrders
  };
}

/**
 * Export tracking data to Excel with merged header and TikTok-compliant format
 * @param {Array<Object>} successResults - Orders with tracking numbers
 * @returns {void} - Downloads Excel file or shows error
 */
export function exportOrdersToTiktokSofExcel(successResults) {
  const XLSX = require('xlsx');

  // Filter only orders with tracking numbers
  const ordersWithTracking = successResults.filter(result => result.trackingNo);

  if (ordersWithTracking.length === 0) {
    console.error('❌ ไม่มีรายการที่มีรหัสติดตามให้ส่งออก');
    return;
  }

  // TikTok template format - Row 1: Instructions (merged A1:E1)
  const tiktokInstructions = `เคล็ดลับด่วน:

1. อัปเดตเทมเพลต:
- ไม่จำเป็นต้องใช้รหัสพัสดุอีกต่อไป คุณสามารถดำเนินการและติดตามคำสั่งซื้อโดยใช้หมายเลขคำสั่งซื้อได้
- มีการเพิ่มรหัส SKU เป็นตัวเลือกใหม่ จะต้องกรอกเมื่อคำสั่งซื้อถูกแบ่งออกเป็นหลายพัสดุเท่านั้น

2. เพื่อดำเนินการตามคำสั่งซื้อที่ยังไม่ได้รวมหรือแยก:
- ตรวจสอบให้แน่ใจว่าคุณกรอกหมายเลขคำสั่งซื้อ ผู้ให้บริการจัดส่ง และเลขพัสดุที่ถูกต้อง มิฉะนั้นลูกค้าของคุณจะไม่สามารถติดตามพัสดุของพวกเขาได้
- รูปแบบเลขพัสดุ: อักขระ 4-50 ตัว รวมถึงตัวพิมพ์ใหญ่และตัวพิมพ์เล็ก ตัวเลข ยัติภังค์ ขีดเส้นใต้ มหัพภาค และทับ
- โปรดทราบว่ารูปแบบเลขพัสดุแตกต่างกันไปตามผู้ให้บริการ อย่าลืมกรอกผู้ให้บริการจัดส่งและเลขพัสดุที่เกี่ยวข้อง

3. เพื่อรวมคำสั่งซื้อ:
- หากคุณมีคำสั่งซื้อตั้งแต่ 2 รายการขึ้นไปที่จะส่งไปยังผู้รับและที่อยู่เดียวกัน คุณสามารถกรอกรหัสการติดตามหนึ่งรายการเพื่อให้พัสดุรวมเข้าด้วยกันเป็นพัสดุเดียว คุณไม่จำเป็นต้องกรอกข้อมูลรหัส SKU สำหรับคำสั่งซื้อเหล่านี้

4. เพื่อแยกคำสั่งซื้อ:
- หากคุณแยกคำสั่งซื้อ คุณสามารถเพิ่มข้อมูลการติดตามได้โดยตรงใน Seller Center
- หากคุณต้องการเพิ่มการจัดส่งที่นี่ ตรวจสอบให้แน่ใจว่าคุณกรอกรหัส SKU และปริมาณรายการทั้งหมดตามที่ต้องการ`;

  // Build the sheet data array
  // Row 1: Instructions (only in A1, B1-E1 will be merged)
  // Row 2: Headers
  // Row 3: Required/Optional
  // Row 4+: Data
  const sheetData = [
    [tiktokInstructions, null, null, null, null],  // Row 1
    ['หมายเลขคำสั่งซื้อ', 'SKU ID', 'ชื่อสินค้า', 'ชื่อผู้ให้บริการจัดส่ง', 'เลขพัสดุ'],  // Row 2
    ['Required', 'Optional', 'Optional', 'Required', 'Required']  // Row 3
  ];

  // Add data rows (Row 4+)
  ordersWithTracking.forEach(result => {
    sheetData.push([
      result.orderId,           // Order ID
      result.productSku,                        // SKU ID (blank)
      result.productName,                        // Product Name (blank)
      'Shipped by seller',       // Shipper
      result.trackingNo || "N/A"          // Tracking Number
    ]);
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // Merge A1:E1 for instructions
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];

  // Set column widths (matching TikTok template)
  ws['!cols'] = [
    { wch: 40 }, // A: Order ID
    { wch: 13 }, // B: SKU ID
    { wch: 20 }, // C: Product Name
    { wch: 13 }, // D: Shipper
    { wch: 13 }  // E: Tracking Number
  ];

  // Set row heights (Row 1 = 270 for instructions)
  ws['!rows'] = [{ hpt: 270 }];

  // Create workbook with sheet named "Shipping info"
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Shipping info");

  // Add shipping_provider_name_drop_lis sheet (required by TikTok)
  const providerData = [
    ['Shipped by seller', null, 'Attention: Any modifications to this page are not allowed.\nModifications may result in files not being parsed correctly.']
  ];
  const wsProvider = XLSX.utils.aoa_to_sheet(providerData);
  XLSX.utils.book_append_sheet(wb, wsProvider, "shipping_provider_name_drop_lis");

  // Add meta_info_sheet (required by TikTok)
  const now = new Date();
  const createTime = now.getTime();
  const logId = now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14) + 'DC7B9F3148B606C13B';
  const taskId = '1' + String(createTime).slice(0, 12) + '672648657';

  const metaInfo = JSON.stringify({
    "export_type": 4,
    "type_version": "sea_001",
    "lang": "th-TH",
    "sheet_name_map": {
      "data_sheet": "Shipping info",
      "shipping_provider_name": "shipping_provider_name_drop_lis"
    },
    "data_header_map": {
      "main_order_id": "หมายเลขคำสั่งซื้อ",
      "product_name": "ชื่อสินค้า",
      "shipping_provider_name": "ชื่อผู้ให้บริการจัดส่ง",
      "sku_id": "SKU ID",
      "tracking_id": "เลขพัสดุ"
    },
    "sorted_header_id": ["main_order_id", "sku_id", "product_name", "shipping_provider_name", "tracking_id"],
    "header_begin_row": 1,
    "header_row_count": 2,
    "data_begin_row": 3,
    "create_file_log_id": logId,
    "export_task_id": taskId,
    "create_time_utc": createTime
  });

  const metaData = [
    [metaInfo, null, 'Attention: Any modifications to this page are not allowed.\nModifications may result in files not being parsed correctly.']
  ];
  const wsMeta = XLSX.utils.aoa_to_sheet(metaData);
  XLSX.utils.book_append_sheet(wb, wsMeta, "meta_info_sheet");

  // Generate filename with current date/time
  const dateStr = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
  const filename = `tiktok_tracking_${dateStr}.xlsx`;

  // Save file
  XLSX.writeFile(wb, filename);

  console.log(`✅ ส่งออกสำเร็จ! ดาวน์โหลด ${ordersWithTracking.length} รายการแล้ว`);
}

export function exportOrdersToTiktokSofDeliveredExcel(successResults) {
  const XLSX = require('xlsx');

  // Filter only orders with tracking numbers
  const ordersWithTracking = successResults.filter(result => result.trackingNo);
  // const ordersWithTracking = successResults;

  if (ordersWithTracking.length === 0) {
    console.error('❌ ไม่มีรายการที่มีรหัสติดตามให้ส่งออก');
    return;
  }

  // TikTok template format - Row 1: Instructions (merged A1:E1)
  const tiktokInstructions = `คำแนะนำ: 
1. ตรวจสอบให้แน่ใจว่าได้อัปเดตสถานะของพัสดุที่จัดส่งสำเร็จหรือรายการการจัดส่งที่ไม่สำเร็จ มิฉะนั้น ลูกค้าอาจร้องเรียน
2. หากจัดส่งพัสดุสำเร็จแล้ว ให้อัปเดตสถานะการจัดส่งเป็น "จัดส่งแล้ว" ไม่จำเป็นต้องเลือกสาเหตุสำหรับการจัดส่งที่ไม่สำเร็จ
3. หากการจัดส่งพัสดุไม่สำเร็จ ให้อัปเดตสถานะการจัดส่งเป็น "จัดส่งไม่สำเร็จ" และเลือกสาเหตุสำหรับการจัดส่งที่ไม่สำเร็จ สำหรับพัสดุที่ระบุว่าจัดส่งไม่สำเร็จ ระบบจะคืนเงินให้ลูกค้าโดยอัตโนมัติและไม่สามารถยกเลิกได้ โปรดเลือกสถานะนี้ด้วยความระมัดระวัง
4. หากยังไม่ได้จัดส่งพัสดุและคุณต้องการอัปเดตสถานะการจัดส่งภายหลัง คุณสามารถเว้นข้อมูลการจัดส่งว่างไว้ได้และไม่จำเป็นต้องเลือกสาเหตุสำหรับการจัดส่งที่ไม่สำเร็จ`;

  // Build the sheet data array
  // Row 1: Instructions (only in A1, B1-G1 will be merged)
  // Row 2: Headers
  // Row 3+: Data
  const sheetData = [
    [tiktokInstructions, null, null, null, null, null, null],  // Row 1
    ['Order ID', 'Package ID', 'Product Name', 'Shipping Provider Name', 'Tracking ID', 'Delivery Result', 'Delivery Failed Reason'],  // Row 2
  ];

  // Add data rows (Row 4+)
  ordersWithTracking.forEach(result => {
    let packageId = result.productSku;
    if (packageId.includes("___")) packageId = packageId.split("___")[0];
    sheetData.push([
      result.orderId,           // Order ID
      packageId,               // Package ID (blank)
      result.productName,      // Product Name (blank)
      'Shipped by seller',      // Shipping Provider Name
      result.trackingNo,        // Tracking ID
      'Delivered',              // Delivery Result (Delivered)
      ''                        // Delivery Failed Reason (blank)
    ]);
  });

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(sheetData);

  // Merge A1:G1 for instructions
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

  // Set column widths (matching TikTok template)
  ws['!cols'] = [
    { wch: 40 }, // A: Order ID
    { wch: 13 }, // B: SKU ID
    { wch: 20 }, // C: Product Name
    { wch: 13 }, // D: Shipper
    { wch: 13 }  // E: Tracking Number
  ];

  // Set row heights (Row 1 = 270 for instructions)
  ws['!rows'] = [{ hpt: 270 }];

  // Create workbook with sheet named "Shipping info"
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Update delivery status");

  // Sheet 2: Delivery status
  const deliveryStatusData = [
    ['Delivered', null, null, null, null],
    [null, null, null, null, null],
  ];
  const sheet2 = XLSX.utils.aoa_to_sheet(deliveryStatusData);
  XLSX.utils.book_append_sheet(wb, sheet2, "Delivery status");

  // Sheet 3: Delivery failed reason
  const deliveryFailedReasonData = [
    ['it has an invalid shipping address', null, null, null, null],
    ['the customer was unavailable', null, null, null, null],
    ['the customer can\'t be reached', null, null, null, null],
    ['it was declined by the customer', null, null, null, null],
    ['shipping delay', null, null, null, null],
    ['package lost', null, null, null, null],
    ['package damage', null, null, null, null],
    ['a force majeure event occurred', null, null, null, null],
    ['courier encountered an issue', null, null, null, null]
  ];
  const sheet3 = XLSX.utils.aoa_to_sheet(deliveryFailedReasonData);
  XLSX.utils.book_append_sheet(wb, sheet3, "Delivery failed reason");

  // Add meta_info_sheet (required by TikTok)
  const now = new Date();
  const createTime = now.getTime();
  const logId = now.toISOString().replace(/[-:T.Z]/g, '').slice(0, 14) + 'DC7B9F3148B606C13B';
  const taskId = '1' + String(createTime).slice(0, 12) + '672648657';

  const metaInfo = JSON.stringify({
    "export_type": 5,
    "type_version": "old_sof_version",
    "lang": "en-GB",
    "sheet_name_map": {
      "data_sheet": "Update delivery status",
      "delivery_failed_reason": "Delivery failed reason",
      "delivery_result": "Delivery status"
    },
    "data_header_map": {
      "delivery_failed_reason": "Delivery Failed Reason",
      "delivery_result": "Delivery Result",
      "main_order_id": "Order ID",
      "package_id": "Package ID",
      "product_name": "Product Name",
      "shipping_provider_name": "Shipping Provider Name",
      "tracking_id": "Tracking ID"
    },
    "sorted_header_id": ["main_order_id", "package_id", "product_name", "shipping_provider_name", "tracking_id", "delivery_result", "delivery_failed_reason"],
    "header_begin_row": 1,
    "header_row_count": 1,
    "data_begin_row": 2,
    "create_file_log_id": logId,
    "export_task_id": taskId,
    "create_time_utc": createTime
  });

  const metaData = [[metaInfo]];
  const wsMeta = XLSX.utils.aoa_to_sheet(metaData);
  XLSX.utils.book_append_sheet(wb, wsMeta, "meta_info_sheet");

  // Generate filename with current date/time
  const dateStr = now.toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
  const filename = `tt_update_delivery_result_${dateStr}.xlsx`;

  // Save file
  XLSX.writeFile(wb, filename);

  console.log(`✅ ส่งออกสถานะการจัดส่งสำเร็จ! อัปเดต ${ordersWithTracking.length} รายการแล้ว`);
}
